import React, { useEffect, useState } from "react";
import "./AgentCalendar.css";
import DetailsPopup from "./DetailsPopup/DetailsPopup";
import DaySchedule from "./DaySchedule/DaySchedule";
import WeekNavigator from "./WeekNavigator/WeekNavigator";
import CalendarHeader from "./CalendarHeader/CalendarHeader";
import MissionPopup from "./MissionPopup/MissionPopup";
import { useAuth } from "../../../Security/AuthProvider";
import { useNotification } from "../../../Utils/Commons/Notification/NotificationContext";
import { useApiErrorHandler } from "../../../Utils/Commons/apiErrorHandler";

const AgentCalendar = ({ currentAgent }) => {
  const { getCredentials } = useAuth();
  const { notifyError, notifySuccess } = useNotification();
  const { handleFetchError, handleResponseError } = useApiErrorHandler();
  const [rdvs, setRdvs] = useState([[], [], [], [], []]);
  const [dragging, setDragging] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(0);
  const [detailsPopup, setDetailsPopup] = useState(false);
  const [adresse, setAdresse] = useState("");
  const [clientName, setClientName] = useState(null);
  const [appartement, setAppartement] = useState("");

  const [date, setDate] = useState(null);
  const [dayIndex, setDayIndex] = useState(null);
  const [startInterval, setStartInterval] = useState(null);
  const [endInterval, setEndInterval] = useState(null);

  const [startIndex, setStartIndex] = useState(null);
  const [endIndex, setEndIndex] = useState(null);
  const [draggingDayIndex, setDraggingDayIndex] = useState(null);

  const [weekStart, setWeekStart] = useState(null);
  const [weekEnd, setWeekEnd] = useState(null);

  const [missionPopup, setMissionPopup] = useState(false);
  const [selectedMission, setSelectedMission] = useState(null);

  useEffect(() => {
    fetchRdvs();
    calculateWeekDates();
  }, [currentAgent, currentWeek]);

  function getOnlyThisWeek(arrays) {
    const today = new Date();
    const startOfWeek = new Date(
      today.setDate(
        today.getDate() -
          (today.getDay() === 0 ? 6 : today.getDay() - 1) +
          7 * currentWeek
      )
    );
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(today.setDate(startOfWeek.getDate() + 4));
    endOfWeek.setHours(23, 59, 59, 999);

    const weekArray = [[], [], [], [], []];

    arrays.forEach((array) => {
      const date = new Date(array[0].date);
      date.setHours(0, 0, 0, 0);
      const dayIndex = date.getDay();

      if (
        dayIndex >= 1 &&
        dayIndex <= 5 &&
        date >= startOfWeek &&
        date <= endOfWeek
      ) {
        weekArray[dayIndex - 1] = array;
      }
    });
    return weekArray;
  }

  const fetchRdvs = async () => {
    try {
      const response = await fetch(`/rdv/get-rdv-by-agent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_name: currentAgent,
          company_name: getCredentials().company_name,
          company_password: getCredentials().company_password,
        }),
      });
      const data = await response.json();
      const filteredData = getOnlyThisWeek(data);
      setRdvs(filteredData);
    } catch (error) {
      handleFetchError(error, "Échec de la récupération des rendez-vous");
    }
  };

  const handleMouseDown = (time, index, dayIndex) => {
    setDragging(true);
    setStartTime(time);
    setStartIndex(index);
    setDraggingDayIndex(dayIndex);
  };

  // Fonction avec tolérance et correction automatique
  function checkAndCorrectOverlap(start, end, dayIndex) {
    const TOLERANCE_INTERVALS = 2; // Tolérance de 2 intervalles (30 minutes)
    let correctedStart = start;
    let correctedEnd = end;
    let hasCorrected = false;

    for (const rdv of rdvs[dayIndex]) {
      // Vérifier les différents types de chevauchements
      if (start < rdv.ending && end > rdv.beginning) {
        // Il y a un chevauchement, analyser s'il est dans la tolérance
        
        // Cas 1: Le nouveau RDV dépasse au début d'un RDV existant
        if (start < rdv.beginning && end > rdv.beginning && end <= rdv.beginning + TOLERANCE_INTERVALS) {
          correctedEnd = rdv.beginning;
          hasCorrected = true;
          continue;
        }
        
        // Cas 2: Le nouveau RDV dépasse à la fin d'un RDV existant
        if (start >= rdv.ending - TOLERANCE_INTERVALS && start < rdv.ending && end > rdv.ending) {
          correctedStart = rdv.ending;
          hasCorrected = true;
          continue;
        }
        
        // Cas 3: Chevauchement important qui ne peut pas être corrigé
        return { canCreate: false, correctedStart, correctedEnd, hasCorrected: false };
      }
    }

    // Vérifier que le RDV corrigé est encore valide (au moins 1 intervalle de durée)
    if (correctedEnd <= correctedStart) {
      return { canCreate: false, correctedStart, correctedEnd, hasCorrected: false };
    }

    return { 
      canCreate: true, 
      correctedStart, 
      correctedEnd, 
      hasCorrected,
      originalStart: start,
      originalEnd: end
    };
  }

  const handleMouseUp = (date, dayIndex) => {
    if (startTime !== null && endTime !== null) {
      const startInterval = Math.floor(
        (startTime.getHours() * 60 + startTime.getMinutes() - 480) / 15
      );
      // Ajouter 1 pour inclure le dernier intervalle sélectionné
      const endInterval = Math.floor(
        (endTime.getHours() * 60 + endTime.getMinutes() - 480) / 15
      ) + 1;

      // Normaliser les intervalles avant vérification (gestion sélection dans les deux sens)
      const normalizedStart = Math.min(startInterval, endInterval);
      const normalizedEnd = Math.max(startInterval, endInterval);
      
      // Utiliser la nouvelle logique avec tolérance
      const overlapResult = checkAndCorrectOverlap(normalizedStart, normalizedEnd, dayIndex);
      
      if (overlapResult.canCreate) {
        setDetailsPopup(true);
        setDate(date);
        setDayIndex(dayIndex);
        setStartInterval(overlapResult.correctedStart);
        setEndInterval(overlapResult.correctedEnd);
        
        // Informer l'utilisateur si une correction a été appliquée
        const formatInterval = (interval) => {
          const hours = Math.floor(interval * 15 / 60) + 8;
          const minutes = (interval * 15) % 60;
          return `${hours}h${minutes.toString().padStart(2, '0')}`;
        };
        
        // Détecter si il y a eu normalisation (sélection dans le sens inverse)
        const wasNormalized = startInterval > endInterval;
        
        // Détecter si il y a eu correction par la fonction de tolérance
        const hadOverlapCorrection = overlapResult.hasCorrected || 
          (overlapResult.correctedStart !== normalizedStart || overlapResult.correctedEnd !== normalizedEnd);
        
        // Afficher notification si normalisation OU correction
        if (wasNormalized || hadOverlapCorrection) {
          let message;
          
          if (hadOverlapCorrection) {
            // Il y a eu une correction de chevauchement
            message = `Horaire ajusté automatiquement : ${formatInterval(overlapResult.correctedStart)} - ${formatInterval(overlapResult.correctedEnd)} (au lieu de ${formatInterval(normalizedStart)} - ${formatInterval(normalizedEnd)})`;
          } else if (wasNormalized) {
            // Il y a eu seulement une normalisation de l'ordre
            message = `Horaire ajusté automatiquement : ${formatInterval(overlapResult.correctedStart)} - ${formatInterval(overlapResult.correctedEnd)}`;
          }
          
          notifySuccess(message);
        }
      } else {
        notifyError("Créneaux horaires qui se chevauchent - impossible de corriger automatiquement");
      }
    }

    setStartTime(null);
    setEndTime(null);
    setDragging(false);
    setStartIndex(null);
    setEndIndex(null);
    setDraggingDayIndex(null);
  };

  const handleMouseEnter = (time, index) => {
    if (dragging) {
      setEndTime(time);
      setEndIndex(index);
    }
  };

  const createRdv = async (date, dayIndex, start, end) => {
    if (start > end) {
      let temp = start;
      start = end;
      end = temp;
    }

    // Utiliser la logique avec tolérance pour une vérification finale
    const overlapResult = checkAndCorrectOverlap(start, end, dayIndex);
    
    if (!overlapResult.canCreate) {
      notifyError("Impossible de créer le RDV : créneaux qui se chevauchent");
      return;
    }

    // Utiliser les horaires corrigés si nécessaire
    const finalStart = overlapResult.correctedStart;
    const finalEnd = overlapResult.correctedEnd;

    try {
      const newRdv = {
        clientName: clientName || "New Appointment",
        agentName: currentAgent,
        date: date.toISOString().split("T")[0],
        beginning: finalStart,
        ending: finalEnd,
        adresse: adresse || "Location not specified",
        appartement: appartement || "",
        company_name: getCredentials().company_name,
        company_password: getCredentials().company_password,
      };

      const response = await fetch("/rdv/new-rdv", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newRdv),
      });

      if (response.ok) {
        fetchRdvs();
        notifySuccess("Rendez-vous créé avec succès");
              } else {
          handleResponseError(response, "Échec de la création du rendez-vous");
        }
      } catch (error) {
        handleFetchError(error, "Erreur lors de la création du rendez-vous");
      }
  };

  const deleteRdv = async (index, dayIndex) => {
    if (rdvs.length === 0) return;
    const rdvToDelete = rdvs[dayIndex].find((rdv) => rdv.beginning === index);
    if (rdvToDelete) {
      const rdvId = rdvToDelete.id;
      try {
        const response = await fetch(`/rdv/delete-rdv`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: rdvId,
            company_name: getCredentials().company_name,
            company_password: getCredentials().company_password,
          }),
        });
        if (response.ok) {
          fetchRdvs();
          notifySuccess("Rendez-vous supprimé avec succès");
                  } else {
            handleResponseError(response, "Échec de la suppression du rendez-vous");
          }
        } catch (error) {
          handleFetchError(error, "Erreur lors de la suppression du rendez-vous");
        }
    }
  };

  const generateTimeSlots = () => {
    function startOfWeek(date) {
      var diff =
        date.getDate() +
        currentWeek * 7 -
        date.getDay() +
        (date.getDay() === 0 ? -6 : 1);

      return new Date(date.setDate(diff));
    }

    const slots = [];
    const startOfDay = startOfWeek(new Date());
    startOfDay.setHours(8, 0, 0, 0);

    for (let day = 0; day < 5; day++) {
      const daySlots = [];
      const currentDay = new Date(startOfDay);
      currentDay.setDate(currentDay.getDate() + day);
      for (let i = 0; i < 36; i++) {
        const time = new Date(currentDay);
        time.setMinutes(time.getMinutes() + i * 15);

        const isRdvStart = rdvs[day].some((rdv) => {
          return i === rdv.beginning;
        });
        daySlots.push({
          time: time,
          interval: i,
          isRdvStart: isRdvStart,
          associatedRdv: rdvs[day].find(
            (rdv) => i >= rdv.beginning && i < rdv.ending
          ),
        });
      }
      slots.push(daySlots);
    }
    return slots;
  };

  useEffect(() => {
    setTimeSlots(generateTimeSlots());
  }, [rdvs]);

  const handleSubmit = () => {
    createRdv(date, dayIndex, startInterval, endInterval);
    setDate(null);
    setDayIndex(null);
    setStartInterval(null);
    setEndInterval(null);
    setDetailsPopup(false);
  };

  const handleCancel = () => {
    setDate(null);
    setDayIndex(null);
    setStartInterval(null);
    setEndInterval(null);
    setDetailsPopup(false);
  };

  const calculateWeekDates = () => {
    const today = new Date();
    const startOfWeek = new Date(
      today.setDate(
        today.getDate() -
          (today.getDay() === 0 ? 6 : today.getDay() - 1) +
          7 * currentWeek
      )
    );
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(today.setDate(startOfWeek.getDate() + 4));
    endOfWeek.setHours(23, 59, 59, 999);

    setWeekStart(startOfWeek);
    setWeekEnd(endOfWeek);
  };

  const changeWeek = (offset) => {
    setCurrentWeek(currentWeek + offset);
  };

  const openMissionPopup = (rdv) => {
    setSelectedMission(rdv);
    setMissionPopup(true);
  };

  const closeMissionPopup = () => {
    setSelectedMission(null);
    setMissionPopup(false);
  };

  const handleMissionUpdate = (updatedMission) => {
    // Mettre à jour le RDV dans le state local
    setRdvs(prevRdvs => {
      return prevRdvs.map(dayRdvs => {
        return dayRdvs.map(rdv => {
          if (rdv.id === updatedMission.id) {
            return updatedMission;
          }
          return rdv;
        });
      });
    });
    
    // Rafraîchir les données depuis le serveur
    fetchRdvs();
  };

  return (
    <div className="agent-calendar">
      <header className="calendar-header-container">
        <CalendarHeader agentName={currentAgent} />
      </header>
      <div className="agent-schedule">
        <WeekNavigator
          changeWeek={changeWeek}
          weekStart={weekStart}
          weekEnd={weekEnd}
        />
        {detailsPopup && (
          <DetailsPopup
            setAdresse={setAdresse}
            setClientName={setClientName}
            setAppartement={setAppartement}
            handleSubmit={handleSubmit}
            handleCancel={handleCancel}
            adresse={adresse}
            appartement={appartement}
          />
        )}
        <div className="days-container">
          <div className="days-header">
            <div className="empty-day day-header"></div>
            {["Lun.", "Mar.", "Mer.", "Jeu.", "Ven."].map((day, index) => (
              <div className="day-header" key={index}>
                <h3>{day}</h3>
                <h4>
                  {timeSlots[index]
                    ? timeSlots[index][0].time.getDate() +
                      " " +
                      timeSlots[index][0].time.toLocaleDateString("fr-FR", {
                        month: "short",
                      })
                    : ""}
                </h4>
              </div>
            ))}
          </div>
          <div className="days-content">
            <div className="hour-container">
              {Array.from({ length: 36 }, (_, index) => (
                <div className="hour" key={index}>
                  <p className="hour-text">{index % 4 === 0 ? 8 + index / 4 + ":00" : ""}</p>
                </div>
              ))}
            </div>
            {timeSlots.map((daySlots, dayIndex) => (
              <DaySchedule
                key={dayIndex}
                daySlots={daySlots}
                dayIndex={dayIndex}
                rdvs={rdvs}
                handleMouseDown={handleMouseDown}
                handleMouseUp={handleMouseUp}
                handleMouseEnter={handleMouseEnter}
                openMissionPopup={openMissionPopup}
                deleteRdv={deleteRdv}
                startIndex={startIndex}
                endIndex={endIndex}
                draggingDayIndex={draggingDayIndex}
              />
            ))}
          </div>
        </div>
      </div>
      {missionPopup && selectedMission && (
        <MissionPopup 
          mission={selectedMission} 
          onClose={closeMissionPopup}
          onUpdate={handleMissionUpdate}
        />
      )}
    </div>
  );
};

export default AgentCalendar;
